import { Api } from 'api';
import { ChartData } from 'chart.js';
import React from 'react';
// eslint-disable-next-line import/no-unresolved
import { Chart } from 'react-chartjs-2';
import { useMutation, useQuery } from 'react-query';
import {
  ApiResponse,
  ExpertisePoint,
  AdditionalServiceReportAdminResponse,
  AdditionalServiceReportResponse,
  ReportingQueryCacheKey,
  CustomerAdditionalServiceReportDto,
  AdditionalServiceReportDataResponse,
  UserGroup,
} from 'types';
import {
  Button,
  Center,
  Icon,
  Stat,
  StatGroup,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  useToast,
  Box,
  useColorModeValue,
} from '@chakra-ui/react';
import { RootState } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import { hideLoading, showLoading } from 'features';
import { downloadBlobAsFile } from 'core';
import { DownloadIcon } from '@chakra-ui/icons';
import { createColumnHelper } from '@tanstack/react-table';
import { DataTable } from 'components';

export interface AdminReportExtraPackagesProps {
  beginDate?: Date;
  endDate?: Date;
  expertisePoint?: ExpertisePoint;
  groupType?: UserGroup;
}

interface GraphData {
  dateLabels: string[];
  serviceCountData: number[];
}

type AdditionalServiceReportChartData = ChartData<'bar', number[], string>;
type AdditionalServiceReportData = {
  graphData: AdditionalServiceReportChartData;
  isEmpty: boolean;
  totalCount?: number;
  totalAmount?: number;
  services?: CustomerAdditionalServiceReportDto[];
  tableData?: AdditionalServiceReportDataResponse[];
};

export const AdminReportExtraPackages: React.FC<AdminReportExtraPackagesProps> = ({
  beginDate,
  endDate,
  expertisePoint,
  groupType,
}) => {
  const authUserInfo = useSelector((state: RootState) => state.auth.authUserInfo);
  const toast = useToast();
  const dispatch = useDispatch();
  // Color values for the table
  const tableHeaderBg = useColorModeValue('gray.700', 'gray.600');
  const tableHeaderText = useColorModeValue('white', 'white');
  const { data: reportData, isLoading } = useQuery(
    [
      ReportingQueryCacheKey.GetAdditionalServiceReport,
      beginDate?.getTime(),
      endDate?.getTime(),
      expertisePoint?.id,
      groupType,
    ],
    async (): Promise<AdditionalServiceReportData | null> => {
      if (beginDate && endDate) {
        const response: ApiResponse<AdditionalServiceReportResponse | AdditionalServiceReportAdminResponse> =
          authUserInfo?.isSuperAdmin || authUserInfo?.isAdmin
            ? await Api.Reporting.getAdditionalServiceReportAdmin(
                beginDate.getTime(),
                endDate.getTime(),
                expertisePoint?.id,
                groupType,
              )
            : await Api.Reporting.getAdditionalServiceReportAdmin(beginDate.getTime(), endDate.getTime());

        const tableDataResponse = await Api.Reporting.getAdditionalServiceReportData(
          beginDate.getTime(),
          endDate.getTime(),
          expertisePoint?.id,
          groupType,
        );

        const queryData = response.data;
        const graphData: GraphData = {
          dateLabels: [],
          serviceCountData: [],
        };

        // Group services by date
        const servicesByDate = new Map<string, number>();
        queryData.services.forEach((service) => {
          const date = new Date(service.date);
          const dateKey = `${date.getDate()} ${date.toLocaleString(window.navigator.language, { month: 'long' })}`;
          const currentCount = servicesByDate.get(dateKey) || 0;
          servicesByDate.set(dateKey, currentCount + Number(service.count));
        });

        // Convert map to arrays for chart
        servicesByDate.forEach((count, dateLabel) => {
          graphData.dateLabels.push(dateLabel);
          graphData.serviceCountData.push(count);
        });

        return {
          isEmpty: graphData.dateLabels.length === 0,
          graphData: {
            labels: graphData.dateLabels,
            datasets: [
              {
                type: 'bar' as const,
                label: 'Ek Hizmetler',
                backgroundColor: 'rgb(75, 192, 192)',
                data: graphData.serviceCountData,
                borderColor: 'white',
                borderWidth: 2,
              },
            ],
          },
          totalCount: queryData.totalCount,
          totalAmount: queryData.totalAmount,
          services: queryData.services,
          tableData: tableDataResponse.data || [],
        };
      }
      return null;
    },
    {
      retry: 0,
      refetchOnWindowFocus: false,
      enabled: !!(beginDate && endDate),
    },
  );

  const getExportAdditionalServiceReport = useMutation<any, any>(
    async () => {
      dispatch(showLoading());
      if (beginDate && endDate) {
        const response =
          authUserInfo?.isSuperAdmin || authUserInfo?.isAdmin
            ? await Api.Reporting.exportAdditionalServiceReportAdmin(
                beginDate.getTime(),
                endDate.getTime(),
                expertisePoint?.id,
                groupType,
              )
            : await Api.Reporting.exportAdditionalServiceReportAdmin(
                beginDate.getTime(),
                endDate.getTime(),
                undefined,
                groupType,
              );
        if (!response) throw new Error('No response');
        return response;
      }
      return null;
    },
    {
      onSuccess(data) {
        if (data) {
          const date = new Date();
          const formattedDate = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
          const fileName = `EkHizmetler${formattedDate}.xlsx`;
          downloadBlobAsFile(data, fileName);
        } else {
          toast({
            status: 'info',
            title: 'Rapor Bulunamadı',
            isClosable: true,
          });
        }
      },
      onSettled() {
        dispatch(hideLoading());
      },
      onError() {
        toast({
          status: 'error',
          title: 'Rapor İndirilirken Hata Oluştu',
          isClosable: true,
        });
      },
    },
  );

  const columnHelper = createColumnHelper<AdditionalServiceReportDataResponse>();

  const columns = [
    columnHelper.accessor('date', {
      header: 'Tarih',
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('expertisePointName', {
      header: 'Bayi Adı',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('plate', {
      header: 'Plaka',
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('customerName', {
      header: 'Müşteri Adı',
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('count', {
      header: 'Adet',
      cell: (info) => info.getValue(),
      meta: {
        isNumeric: true,
      } as any,
    }),
    columnHelper.accessor('amount', {
      header: 'Tutar',
      cell: (info) => info.getValue(),
      meta: {
        isNumeric: true,
      } as any,
    }),
    columnHelper.accessor('productName', {
      header: 'Ürün Adı',
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('packageGroupName', {
      header: 'Paket Grup Adı',
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('packageName', {
      header: 'Paket Adı',
      cell: (info) => info.getValue() || '-',
    }),
  ];

  if (!reportData || isLoading) {
    return null;
  }

  if (reportData.isEmpty) {
    return (
      <Center>
        <Text fontSize="2xl" mt={2} mb={6}>
          Seçtiğiniz Tarih Aralığında Veri Bulunmuyor!
        </Text>
      </Center>
    );
  }

  return (
    <>
      <Button
        variant="solid"
        leftIcon={<Icon as={DownloadIcon} />}
        onClick={() => getExportAdditionalServiceReport.mutate()}
      >
        Excel İndir
      </Button>

      {beginDate && endDate && reportData && (
        <StatGroup p={4}>
          <Stat>
            <StatLabel>Toplam Ek Hizmet</StatLabel>
            <StatNumber>{reportData.totalCount}</StatNumber>
            <StatHelpText>
              {beginDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
            </StatHelpText>
          </Stat>
          <Stat>
            <StatLabel>Toplam Tutar</StatLabel>
            <StatNumber>{reportData.totalAmount} ₺</StatNumber>
            <StatHelpText>
              {beginDate.toLocaleDateString()} - {endDate.toLocaleDateString()}
            </StatHelpText>
          </Stat>
        </StatGroup>
      )}

      {reportData.tableData && reportData.tableData.length > 0 && (
        <Box mt={6} overflowX="auto">
          <DataTable
            data={reportData.tableData}
            columns={columns}
            usePagination
            tableProps={{
              variant: 'simple',
              colorScheme: 'blue',
              size: 'sm',
              sx: {
                'thead tr': {
                  backgroundColor: tableHeaderBg,
                },
                'thead th': {
                  color: tableHeaderText,
                },
              },
            }}
          />
        </Box>
      )}

      <Chart
        type="bar"
        data={reportData.graphData}
        options={{
          responsive: true,
          scales: {
            x: {
              type: 'category',
              title: {
                display: true,
                text: 'Tarih',
              },
            },
            y: {
              type: 'linear',
              position: 'left',
              title: {
                display: true,
                text: 'Adet',
              },
            },
          },
        }}
      />
    </>
  );
};
